import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Clock, Edit2, AlertCircle, FileText, CheckCircle } from 'lucide-react';
import { Service, ServiceAddon, ServiceCategory, updateService, deleteService, getServiceAddons, updateServiceAddon, deleteServiceAddon, getServiceCategories, createServiceAddon, createService } from '../lib/services';
// import { FlatRateTemplate, getFlatRateTemplatesByService, deleteFlatRateTemplate } from '../lib/flatRateTemplates'; // Temporarily disabled
// import FlatRateHoursModal from './FlatRateHoursModal'; // Temporarily disabled
import AddAddonModal from './AddAddonModal';
import AddonsLibraryModal from './AddonsLibraryModal';
import WorkflowModal from './WorkflowModal';
import { useBusiness } from '../contexts/BusinessContext';
import { getWorkflowByServiceId, getWorkflowSteps, deleteWorkflow, ServiceWorkflow, WorkflowStep } from '../lib/workflows';
import toast from 'react-hot-toast';

interface ServiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null;
  onUpdate: () => void;
}

const colorOptions = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Teal', value: '#14b8a6' }
];

export default function ServiceDetailsModal({ 
  isOpen, 
  onClose, 
  service, 
  onUpdate 
}: ServiceDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState<{ info?: boolean; pricing?: boolean; addons?: boolean; /* templates?: boolean; */ categories?: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({});
  const [selectedColor, setSelectedColor] = useState(colorOptions[0].value);
  const [priceOptions, setPriceOptions] = useState<{
    [key: string]: { name: string; price: number; duration: { hours: number; minutes: number } }
  }>({});
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [editingAddon, setEditingAddon] = useState<string | null>(null);
  const [editedAddonData, setEditedAddonData] = useState<Partial<ServiceAddon>>({});
  // const [isFlatRateModalOpen, setIsFlatRateModalOpen] = useState(false); // Temporarily disabled
  const [isAddAddonModalOpen, setIsAddAddonModalOpen] = useState(false);
  const [isAddonsLibraryModalOpen, setIsAddonsLibraryModalOpen] = useState(false);
  const [addonsLibraryMode, setAddonsLibraryMode] = useState<'manage' | 'select'>('select');
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  // const [templates, setTemplates] = useState<FlatRateTemplate[]>([]); // Temporarily disabled
  const [hasInfoChanges, setHasInfoChanges] = useState(false);
  const [hasPricingChanges, setHasPricingChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Partial<Service>>({});
  const [originalPriceOptions, setOriginalPriceOptions] = useState<any>({});
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [workflow, setWorkflow] = useState<ServiceWorkflow | null>(null);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);

  useEffect(() => {
    if (isOpen && service) {
      setIsAnimating(true);
      setFormData(service);
      setOriginalFormData({...service});
      setSelectedColor(service.color || colorOptions[0].value);
      
      // Initialize price options from service data
      const prices: {
        [key: string]: { name: string; price: number; duration: { hours: number; minutes: number } }
      } = {};
      service.price_options?.forEach(po => {
        const hours = Math.floor(po.duration_minutes / 60);
        const minutes = po.duration_minutes % 60;
        prices[po.id || po.name] = {
          name: po.name || 'Default',
          price: po.price,
          duration: { hours, minutes }
        };
      });

      // Ensure at least one price option exists
      if (Object.keys(prices).length === 0) {
        prices['default'] = {
          name: 'Default',
          price: 0,
          duration: { hours: 1, minutes: 0 }
        };
      }

      setPriceOptions(prices);
      setOriginalPriceOptions(JSON.parse(JSON.stringify(prices)));
      setHasInfoChanges(false);
      setHasPricingChanges(false);

      // Load add-ons, templates, categories, and workflow
      loadAddons();
      // loadTemplates(); // Temporarily disabled
      loadCategories();
      loadWorkflow();
    }
  }, [isOpen, service, selectedBusiness?.id]);

  useEffect(() => {
    if (Object.keys(originalFormData).length > 0) {
      setHasInfoChanges(
        formData.name !== originalFormData.name ||
        formData.description !== originalFormData.description ||
        formData.category_id !== originalFormData.category_id
      );
    }
  }, [formData, originalFormData]);

  useEffect(() => {
    if (Object.keys(originalPriceOptions).length > 0) {
      // Check if keys are different
      const originalKeys = Object.keys(originalPriceOptions);
      const currentKeys = Object.keys(priceOptions);
      
      if (originalKeys.length !== currentKeys.length) {
        setHasPricingChanges(true);
        return;
      }
      
      // Check if any options have changed
      let hasChanges = false;
      for (const key of currentKeys) {
        if (!originalPriceOptions[key] || 
            priceOptions[key].name !== originalPriceOptions[key].name ||
            priceOptions[key].price !== originalPriceOptions[key].price ||
            priceOptions[key].duration.hours !== originalPriceOptions[key].duration.hours ||
            priceOptions[key].duration.minutes !== originalPriceOptions[key].duration.minutes) {
          hasChanges = true;
          break;
        }
      }
      
      setHasPricingChanges(hasChanges);
    }
  }, [priceOptions, originalPriceOptions]);

  const loadAddons = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      const addonsData = await getServiceAddons(service.id);
      setAddons(addonsData);
    } catch (err) {
      console.error('Error loading add-ons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load add-ons');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  // Temporarily disabled - flat rate templates
  /*
  const loadTemplates = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, templates: true }));
      const templatesData = await getFlatRateTemplatesByService(service.id);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };
  */

  const loadCategories = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(prev => ({ ...prev, categories: true }));
      const categoriesData = await getServiceCategories(selectedBusiness.id);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(prev => ({ ...prev, categories: false }));
    }
  };

  const loadWorkflow = async () => {
    if (!service?.id) return;

    try {
      const workflowData = await getWorkflowByServiceId(service.id);
      if (workflowData) {
        setWorkflow(workflowData);
        const steps = await getWorkflowSteps(workflowData.id);
        setWorkflowSteps(steps);
      } else {
        setWorkflow(null);
        setWorkflowSteps([]);
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      setWorkflow(null);
      setWorkflowSteps([]);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflow) return;

    if (confirm('Are you sure you want to delete this workflow?')) {
      try {
        await deleteWorkflow(workflow.id);
        setWorkflow(null);
        setWorkflowSteps([]);
      } catch (error) {
        console.error('Error deleting workflow:', error);
        setError('Failed to delete workflow');
      }
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSaveInfo = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, info: true }));
      setError(null);

      const updates: Partial<Service> = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        color: selectedColor,
        tax_exempt: formData.tax_exempt
      };

      await updateService(service.id, updates);
      await onUpdate();
    } catch (err) {
      console.error('Error updating service info:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service information');
    } finally {
      setLoading(prev => ({ ...prev, info: false }));
    }
  };

  const handleSavePricing = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, pricing: true }));
      setError(null);

      // Convert price options to the format expected by the API
      const priceOptionUpdates = Object.entries(priceOptions).map(([id, option]) => ({
        name: option.name || 'Default',
        price: option.price,
        duration_minutes: (option.duration.hours * 60) + option.duration.minutes,
        service_id: service.id
      }));

      await updateService(service.id, {
        price_options: priceOptionUpdates
      });
      await onUpdate();
    } catch (err) {
      console.error('Error updating service pricing:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service pricing');
    } finally {
      setLoading(prev => ({ ...prev, pricing: false }));
    }
  };

  const handleAddPrice = () => {
    const newId = `new-${Date.now()}`;
    setPriceOptions(prev => ({
      ...prev,
      [newId]: { 
        name: 'New Option',
        price: 0, 
        duration: { hours: 1, minutes: 0 } 
      }
    }));
  };

  const handleUpdatePriceName = (id: string, name: string) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        name: name || 'Default'
      }
    }));
  };

  const handleUpdatePrice = (id: string, price: number) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        price
      }
    }));
  };

  const handleUpdateDuration = (id: string, field: 'hours' | 'minutes', value: number) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        duration: {
          ...prev[id].duration,
          [field]: value
        }
      }
    }));
  };

  const handleDeletePrice = (id: string) => {
    if (Object.keys(priceOptions).length > 1) {
      const newPrices = { ...priceOptions };
      delete newPrices[id];
      setPriceOptions(newPrices);
    }
  };

  const handleEditAddon = (addon: ServiceAddon) => {
    setEditingAddon(addon.id);
    setEditedAddonData({
      name: addon.name,
      description: addon.description,
      price: addon.price,
      duration_minutes: addon.duration_minutes
    });
  };

  const handleSaveAddon = async (addonId: string) => {
    try {
      setLoading(prev => ({ ...prev, addons: true }));
      await updateServiceAddon(addonId, editedAddonData);
      await loadAddons();
      setEditingAddon(null);
      setEditedAddonData({});
    } catch (err) {
      console.error('Error updating add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to update add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  const handleDeleteAddon = async (addonId: string) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      await deleteServiceAddon(addonId);
      await loadAddons();
    } catch (err) {
      console.error('Error deleting add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  // Temporarily disabled - flat rate templates
  /*
  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, templates: true }));
      await deleteFlatRateTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };
  */

  const handleCreateAddon = async (addonData: Partial<ServiceAddon>) => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      // TODO: Implement addon creation
      await loadAddons();
    } catch (err) {
      console.error('Error creating add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!service) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`fixed inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div className="h-screen flex flex-col">
          {/* Header with Mobile-Optimized Design */}
          <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
            <div className="flex items-center justify-between px-2 py-2 sm:px-4 sm:py-4">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <button 
                  onClick={handleClose} 
                  className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                  style={{ minWidth: '32px', minHeight: '32px' }}
                >
                  <X className="h-4 w-4 sm:h-6 sm:w-6" />
                </button>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-white truncate">
                    {service?.name}
                  </h2>
                  <div className="flex items-center mt-0.5">
                    <span className="text-xs sm:text-sm text-gray-300 truncate">
                      {service?.category?.name || 'Edit service details'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content with mobile-optimized styling */}
          <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-2xl mx-auto py-1 px-2 sm:py-6 sm:px-6">
              {error && (
                <div className="mb-3 sm:mb-6 backdrop-blur-xl bg-red-50/90 border border-red-200/50 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm shadow-sm"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              <div className="space-y-1.5 sm:space-y-6">
                {/* Service Information Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Service Information</h3>
                    <button
                      onClick={handleSaveInfo}
                      disabled={loading.info || !hasInfoChanges}
                      className={`inline-flex items-center justify-center px-3 py-2 text-xs font-bold rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ${
                        hasInfoChanges && !loading.info 
                          ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      style={{ minHeight: '32px' }}
                    >
                      {loading.info ? (
                        <>
                          <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                  <div className="p-2 sm:p-6">
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Service Name
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          value={formData.category_id || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                          disabled={loading.categories}
                        >
                          <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        {loading.categories && (
                          <div className="mt-2 text-xs text-gray-500 font-medium">Loading categories...</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={formData.description || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={4}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation resize-none"
                          style={{ 
                            minHeight: '100px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pricing Options Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Pricing Options</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleAddPrice}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100/80 backdrop-blur-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                        style={{ 
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      >
                        <Plus className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={handleSavePricing}
                        disabled={loading.pricing || !hasPricingChanges}
                        className={`inline-flex items-center justify-center px-3 py-2 text-xs font-bold rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ${
                          hasPricingChanges && !loading.pricing
                            ? 'bg-purple-600 hover:bg-purple-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        style={{ minHeight: '32px' }}
                      >
                        {loading.pricing ? (
                          <>
                            <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="p-2 sm:p-6">
                    <div className="space-y-4">
                      {Object.entries(priceOptions).map(([id, data]) => (
                        <div key={id} className="p-3 sm:p-4 bg-purple-50/30 rounded-2xl border border-purple-200/20">
                          <div className="flex items-center justify-between mb-4">
                            <input
                              type="text"
                              value={data.name}
                              onChange={(e) => handleUpdatePriceName(id, e.target.value)}
                              className="text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300"
                              style={{ 
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)'
                              }}
                              placeholder="Enter name"
                            />
                            {Object.keys(priceOptions).length > 1 && (
                              <button
                                onClick={() => handleDeletePrice(id)}
                                className="flex items-center justify-center w-8 h-8 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50/80 transition-all duration-200 touch-manipulation"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                                Price
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                  <span className="text-gray-500 text-base font-medium">$</span>
                                </div>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.price}
                                  onChange={(e) => handleUpdatePrice(id, parseFloat(e.target.value) || 0)}
                                  className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                  style={{ 
                                    minHeight: '44px',
                                    fontSize: '16px',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)'
                                  }}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                                Duration
                              </label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  value={data.duration.hours}
                                  onChange={(e) => handleUpdateDuration(id, 'hours', parseInt(e.target.value) || 0)}
                                  className="w-20 py-3 px-2 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation text-center"
                                  style={{ 
                                    minHeight: '44px',
                                    fontSize: '16px',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)'
                                  }}
                                />
                                <span className="text-sm text-gray-500 font-medium">h</span>
                                <input
                                  type="number"
                                  value={data.duration.minutes}
                                  onChange={(e) => handleUpdateDuration(id, 'minutes', parseInt(e.target.value) || 0)}
                                  className="w-20 py-3 px-2 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation text-center"
                                  style={{ 
                                    minHeight: '44px',
                                    fontSize: '16px',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)'
                                  }}
                                />
                                <span className="text-sm text-gray-500 font-medium">m</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Add-ons Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Add-ons</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setAddonsLibraryMode('manage');
                          setIsAddonsLibraryModalOpen(true);
                        }}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 border border-purple-200 rounded-lg hover:bg-purple-200 transition-all duration-200"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Library
                      </button>
                      <button
                        onClick={() => {
                          setAddonsLibraryMode('select');
                          setIsAddonsLibraryModalOpen(true);
                        }}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100/80 backdrop-blur-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                        style={{ 
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      >
                        <Plus className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                  <div className="p-2 sm:p-6">
                    {loading.addons ? (
                      <div className="py-8 text-center">
                        <div className="inline-flex items-center px-4 py-2 font-semibold text-sm text-gray-500 rounded-2xl bg-gray-100/50">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3"></div>
                          Loading add-ons...
                        </div>
                      </div>
                    ) : addons.length === 0 ? (
                      <div className="py-8 text-center">
                        <div className="text-sm font-semibold text-gray-500 bg-gray-100/50 rounded-2xl py-4 px-6">
                          No add-ons available
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {addons.map((addon) => (
                          <div key={addon.id} className="p-3 sm:p-4 bg-purple-50/30 rounded-2xl border border-purple-200/20">
                            <div className="flex items-center justify-between mb-4">
                              {editingAddon === addon.id ? (
                                <input
                                  type="text"
                                  value={editedAddonData.name || ''}
                                  onChange={(e) => setEditedAddonData(prev => ({ ...prev, name: e.target.value }))}
                                  className="text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 flex-1 mr-3"
                                  style={{ 
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)'
                                  }}
                                  placeholder="Enter name"
                                />
                              ) : (
                                <div className="text-sm font-semibold text-gray-900">
                                  {addon.name}
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                {editingAddon === addon.id ? (
                                  <>
                                    <button
                                      onClick={() => handleSaveAddon(addon.id)}
                                      className="inline-flex items-center justify-center px-3 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingAddon(null);
                                        setEditedAddonData({});
                                      }}
                                      className="inline-flex items-center justify-center px-3 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 bg-gray-100/80 hover:bg-gray-200/80 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEditAddon(addon)}
                                      className="flex items-center justify-center w-8 h-8 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 transition-all duration-200 touch-manipulation"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteAddon(addon.id)}
                                      className="flex items-center justify-center w-8 h-8 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50/80 transition-all duration-200 touch-manipulation"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="space-y-4">
                              {editingAddon === addon.id ? (
                                <>
                                  <textarea
                                    value={editedAddonData.description || ''}
                                    onChange={(e) => setEditedAddonData(prev => ({ ...prev, description: e.target.value }))}
                                    rows={2}
                                    className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation resize-none"
                                    style={{ 
                                      minHeight: '80px',
                                      fontSize: '16px',
                                      backdropFilter: 'blur(10px)',
                                      WebkitBackdropFilter: 'blur(10px)'
                                    }}
                                    placeholder="Enter description"
                                  />
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                                        Price
                                      </label>
                                      <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                          <span className="text-gray-500 text-base font-medium">$</span>
                                        </div>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={editedAddonData.price || ''}
                                          onChange={(e) => setEditedAddonData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                          className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                          style={{ 
                                            minHeight: '44px',
                                            fontSize: '16px',
                                            backdropFilter: 'blur(10px)',
                                            WebkitBackdropFilter: 'blur(10px)'
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                                        Duration (hours)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.5"
                                        value={editedAddonData.duration_minutes ? editedAddonData.duration_minutes / 60 : ''}
                                        onChange={(e) => setEditedAddonData(prev => ({ 
                                          ...prev, 
                                          duration_minutes: Math.round(parseFloat(e.target.value || '0') * 60)
                                        }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="text-sm text-gray-600 font-medium">
                                    {addon.description}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center text-sm text-gray-500">
                                      <Clock className="h-4 w-4 mr-2" />
                                      <span className="font-medium">
                                        {addon.duration_minutes >= 1440 
                                          ? `${Math.round(addon.duration_minutes / 1440)} days`
                                          : `${Math.round(addon.duration_minutes / 60)} hours`
                                        }
                                      </span>
                                    </div>
                                    <div className="text-sm font-bold text-gray-900">
                                      ${addon.price.toLocaleString()}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Flat Rate Templates Section - Temporarily removed from UI */}
                {/* 
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Flat Rate Templates</h3>
                    <button
                      onClick={() => setIsFlatRateModalOpen(true)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100/80 backdrop-blur-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                      style={{ 
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    >
                      <Plus className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                  <div className="p-2 sm:p-6">
                    {loading.templates ? (
                      <div className="py-8 text-center">
                        <div className="inline-flex items-center px-4 py-2 font-semibold text-sm text-gray-500 rounded-2xl bg-gray-100/50">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3"></div>
                          Loading templates...
                        </div>
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="py-8 text-center">
                        <div className="text-sm font-semibold text-gray-500 bg-gray-100/50 rounded-2xl py-4 px-6">
                          No templates available
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {templates.map((template) => (
                          <div key={template.id} className="p-3 sm:p-4 bg-purple-50/30 rounded-2xl border border-purple-200/20">
                            <div className="flex items-center justify-between mb-4">
                              <div className="text-sm font-semibold text-gray-900">
                                {template.title}
                              </div>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="flex items-center justify-center w-8 h-8 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50/80 transition-all duration-200 touch-manipulation"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center text-gray-500">
                                  <Clock className="h-4 w-4 mr-2" />
                                  <span className="font-medium">{template.hours} hours</span>
                                </div>
                                {template.rate_per_hour && (
                                  <div className="font-bold text-gray-900">
                                    ${template.rate_per_hour}/hr
                                  </div>
                                )}
                              </div>
                              {template.description && (
                                <div className="text-sm text-gray-600 font-medium">
                                  {template.description}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                */}

                {/* Workflow Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Workflow</h3>
                    {workflow && (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-2 sm:p-6">
                    {workflow ? (
                      <div>
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">{workflow.name}</h4>
                          {workflowSteps.length > 0 && (
                            <div className="space-y-2">
                              {workflowSteps.map((step, index) => (
                                <div key={step.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-semibold">
                                    {index + 1}
                                  </span>
                                  <span className="text-sm text-gray-700">{step.title}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setIsWorkflowModalOpen(true)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={handleDeleteWorkflow}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm text-gray-600 mb-4">
                          Create a step-by-step workflow template for this service
                        </p>
                        <button
                          onClick={() => setIsWorkflowModalOpen(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                        >
                          Create Workflow
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {service?.id && (
        <>
          {/* Temporarily disabled - flat rate templates
          <FlatRateHoursModal 
            isOpen={isFlatRateModalOpen}
            onClose={() => setIsFlatRateModalOpen(false)}
            serviceId={service.id}
            onSuccess={() => {
              // loadTemplates(); // Temporarily disabled
              onUpdate();
            }}
          />
          */}
          <AddAddonModal
            isOpen={isAddAddonModalOpen}
            onClose={() => setIsAddAddonModalOpen(false)}
            serviceId={service.id}
            onSuccess={() => {
              loadAddons();
              onUpdate();
            }}
          />
          
          <AddonsLibraryModal
            isOpen={isAddonsLibraryModalOpen}
            onClose={() => setIsAddonsLibraryModalOpen(false)}
            mode={addonsLibraryMode}
            serviceId={service.id}
            onSelect={async (selectedAddons) => {
              if (!selectedBusiness?.id) return;
              try {
                // Create service addons sequentially to avoid conflicts
                // Each addon needs its own addon_service_id that references a real service
                for (let index = 0; index < selectedAddons.length; index++) {
                  const addon = selectedAddons[index];
                  
                  console.log('Creating addon service and addon:', {
                    service_id: service.id,
                    name: addon.name,
                    index
                  });
                  
                  // Step 1: Create a placeholder service for this addon
                  const addonService = await createService(
                    {
                      business_id: selectedBusiness.id,
                      name: `${addon.name} (Add-on)`,
                      description: `Add-on service for: ${addon.description || addon.name}`,
                      is_active: false, // Mark as inactive since it's just a placeholder
                      category_id: null,
                      color: '#6B7280', // Gray color for add-on services
                      tax_rate_id: null,
                      tax_exempt: false
                    },
                    [
                      {
                        name: 'Default',
                        price: addon.price,
                        duration_minutes: addon.duration_minutes,
                        order_index: 0
                      }
                    ]
                  );
                  
                  // Step 2: Create the service addon using the new service ID
                  await createServiceAddon({
                    service_id: service.id,
                    business_id: selectedBusiness.id,
                    name: addon.name,
                    description: addon.description || undefined,
                    price: addon.price,
                    duration_minutes: addon.duration_minutes,
                    order_index: index,
                    addon_service_id: addonService.id // Use the created service ID
                  });
                }
                
                // Reload addons and close modal
                loadAddons();
                onUpdate();
                setIsAddonsLibraryModalOpen(false);
                
                const count = selectedAddons.length;
                toast.success(`Added ${count} add-on${count !== 1 ? 's' : ''} to service`);
              } catch (error) {
                console.error('Error adding addons to service:', error);
                toast.error('Failed to add add-ons to service');
              }
            }}
          />
        </>
      )}
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return (
    <>
      {createPortal(modalContent, document.body)}
      {service?.id && (
                  <WorkflowModal
            isOpen={isWorkflowModalOpen}
            onClose={() => setIsWorkflowModalOpen(false)}
            serviceId={service.id}
            serviceName={service.name}
            existingWorkflow={workflow}
            existingSteps={workflowSteps as any}
            onSuccess={() => {
              setIsWorkflowModalOpen(false);
              loadWorkflow(); // Reload workflow after save
            }}
          />
      )}
      
      {/* Add-On Library Modal */}

    </>
  );
}